import { createSelector } from 'reselect';

import { Personas, User } from '@topconagriculture/frontend-utils';
import { getUserProfile } from '@topconagriculture/profiling';

import { RootState } from '../../reducers';
import { UserRoles } from './company.user.reducer';
import { getOperators } from '../operators/operators.selector';

export type UnitOfMeasurementType = {
  temperature: string;
  speed: string;
};

export const getCompanyUserList = (state: RootState): User[] => state.entities.companyUser.companyUserList;
export const getNewUserId = (state: RootState): string | null => state.entities.companyUser.newUserId;

export const getFilteredCompanyUserList = createSelector(
  [getCompanyUserList, getUserProfile],
  (companyUserList, user) => companyUserList.filter((companyUsers) => companyUsers.id !== user!.id),
);

export const getCompanyUserById = createSelector([getCompanyUserList], (users) => (id) => {
  return users.find((user) => user.id === id);
});

export const getCompanyUsersNotBindedToDrivers = createSelector(
  [getCompanyUserList, getOperators],
  (users, operators) => {
    const bindedUsers = operators.map((operator) => operator.tapUserId).filter((id) => id);
    return users.filter((user) => !bindedUsers.includes(user.id));
  },
);

export const getRoles = createSelector([getUserProfile], (user: User): string[] => {
  return user ? user.roles : [];
});

export const hasPermission = createSelector(
  [getRoles],
  (roles: string[]) =>
    (permissionToCheck: string): boolean =>
      roles && roles.includes(permissionToCheck),
);

export const getUserCompanyRole = createSelector([getRoles], (userRoles: string[]): string | null => {
  const availableRoles: string[] = [UserRoles.ADMIN.toLowerCase(), UserRoles.STANDARD.toLowerCase()];
  const foundRole: string[] | undefined =
    userRoles && userRoles.filter((role) => availableRoles.includes(role.toLowerCase()));
  if (!foundRole) {
    return null;
  }
  return foundRole.includes(UserRoles.ADMIN) ? UserRoles.ADMIN : UserRoles.STANDARD;
});

export const getUserPersonas = createSelector([getUserProfile], (user: User): Personas | null => {
  return user ? user.personas : null;
});

export const isDealer = createSelector(
  [getUserPersonas],
  (personas: Personas): boolean => personas === Personas.DEALER,
);

export const isSubDealer = createSelector(
  [getUserPersonas],
  (personas: Personas): boolean => personas === Personas.SUBDEALER,
);

export const isCustomer = createSelector(
  [getUserPersonas],
  (personas: Personas): boolean => personas === Personas.CUSTOMER,
);

export const isAdmin = createSelector([getUserCompanyRole], (role: UserRoles): boolean => role === UserRoles.ADMIN);

export const isStandardUser = createSelector(
  [getUserCompanyRole],
  (role: UserRoles): boolean => role === UserRoles.STANDARD,
);

export const isSupport = createSelector(
  [getUserPersonas],
  (personas: Personas | null): boolean => personas === Personas.SUPPORT || personas === Personas.SUPPORT_MANAGER,
);
